import { defineStore } from "pinia";
import { RouteRecordRaw } from "vue-router";
import router, { routes } from "@/router";
import axios, { ResponseType } from "@/utils/request";
import { Http_getUserAsset } from "@/http/apiFunction";

interface personInfo_Ts {
  id: number;
  name: string;
  phone: string;
  account: string;
  storeId: number;
  storeName: string;
  enableFlag: number;
  roleList: Array<any>;
  roleName: string;
  cityId: number;
  currencyType: number;
  agencyType: number;
  agencyName: string;
  agencyNameHK: string;
  agencyId: number | null;
  parentAgencyId: number | null;
  agencyCode: string;
  parentAgencyName: string;
  cityType: number;
  defaultSetting: defaultSetting_Ts; // 用户默认配置信息
  managerUnitId: number;
  saleFutureDate: boolean; // 是否可以销售未来日期的票
}

interface defaultSetting_Ts {
  departureCityId: number;
  departureStationId: number;
  arrivalCityId: number;
  arrivalStationId: number;
  departureCityName: string;
  departureStationName: string;
  arrivalCityName: string;
  arrivalStationName: string;
  carType: number;
  number: number;
}

export interface roleType {
  code: string;
  name: string;
  permissionList: [];
}

export interface BreadcrumbListItem {
  title: string;
  path: string;
}

export interface IProductItem {
  id: number;
  title: string;
  inventory: number;
  quantity: number;
}

export interface UserAsset {
  id: number;
  type: number;
  currencyType: number;
  accountBalance: number; // 若预付款剩余额度
  openTicketInventory: number; // 买断账户剩余数量
  creditLimit: number; // 信用额度
}

export interface TState {
  personInfo: personInfo_Ts;
  roles: Array<string>;
  menus: Array<RouteRecordRaw>;
  selectMenus: string;
  breadcrumbList: any[];
  menufilter: any[];
  postPort: any;
  userAsset: UserAsset;
  permissionLoaded: boolean; // 权限是否已加载
}

let list: Array<BreadcrumbListItem> = [];
try {
  const l = sessionStorage.getItem("bread_crumb_list");
  if (l) {
    list = JSON.parse(l);
  }
} catch (error) {
  console.log(error);
}
const languageIsen = localStorage.getItem("language") === "en";
export const useUserStore = defineStore({
  id: "user",
  state: (): TState => ({
    userAsset: {
      id: 0,
      type: 0,
      currencyType: 0,
      accountBalance: 0,
      openTicketInventory: 0,
      creditLimit: 0,
    },
    personInfo: {
      id: 0,
      name: "",
      phone: "",
      account: "",
      storeId: 0,
      storeName: "",
      enableFlag: 0,
      roleList: [],
      roleName: "",
      cityId: 0,
      managerUnitId: 0,
      currencyType: 0,
      agencyType: 0,
      agencyName: "",
      agencyNameHK: "",
      agencyId: null,
      parentAgencyId: null,
      agencyCode: "",
      parentAgencyName: "",
      cityType: 0, // 城市类型：1-香港，2-澳门，3-内地
      saleFutureDate: true, // 是否可以销售未来日期的票
      defaultSetting: {
        departureCityId: 0,
        departureStationId: 0,
        arrivalCityId: 0,
        arrivalStationId: 0,
        departureCityName: "",
        departureStationName: "",
        arrivalCityName: "",
        arrivalStationName: "",
        carType: 1,
        number: 1,
      }, // 用户默认配置信息
    },
    roles: [],
    menus: [],
    selectMenus: "",
    breadcrumbList: list || [],
    menufilter: [
      //缓存菜单路由
      {
        title: languageIsen ? "Order inquiry" : "订单查询",
        path: "/order-tickets-management/order-list-finance",
        name: "order-list-finance",
      },
    ],
    postPort: null,
    permissionLoaded: false, // 权限是否已加载
  }),
  getters: {
    includeComponent: (state): any => {
      const list = state.breadcrumbList.concat(state.menufilter);
      return list.map((item: any) => item.name);
    },
  },
  actions: {
    updatePersonInfo(value: personInfo_Ts) {
      this.personInfo = value;
    },
    updateRoles(value: TState["roles"]) {
      this.roles = value;
    },
    updateMenus(value: TState["menus"]) {
      this.menus = value;
    },
    updateSelectMenus(value: string) {
      this.selectMenus = value;
    },
    updatePageTitle(value: string) {
      const l = this.breadcrumbList.length;
      this.breadcrumbList[l - 1].title = value;
    },
    updateDefaultSetting(value: defaultSetting_Ts) {
      this.personInfo.defaultSetting = value;
    },
    updateBreadcrumbList(value: TState["breadcrumbList"]) {
      this.breadcrumbList = value;
      try {
        sessionStorage.setItem(
          "bread_crumb_list",
          JSON.stringify(this.breadcrumbList)
        );
        // eslint-disable-next-line no-empty
      } catch (error) {}
    },
    addBreadcrumbList(value: any) {
      const index = this.breadcrumbList.findIndex(
        (item) => item.path === value.path
      );
      if (index > -1) {
        // 页面回退
        this.breadcrumbList.splice(index + 1);
      } else {
        // 页面前进
        this.breadcrumbList.push(value);
      }

      try {
        sessionStorage.setItem(
          "bread_crumb_list",
          JSON.stringify(this.breadcrumbList)
        );
        // eslint-disable-next-line no-empty
      } catch (error) {}
    },
    // updatePostPort(value: any) {
    //   this.postPort = value;
    // },

    async getMyRole(check?: string) {
      // 如果权限已加载，直接返回
      if (this.permissionLoaded) {
        console.log("权限已加载，跳过重复请求");
        return;
      }

      const res: ResponseType = await axios.get(
        "/facade/sale/agency/user/queryPermission"
      );
      if (res.code === 0) {
        const { permissionList } = res.data;
        const accessedRoutes: any[] = filterRoutesByPermission(
          routes,
          permissionList
        );
        // 过滤出有权限的页面，
        this.updateMenus(accessedRoutes);
        this.updateRoles(permissionList);
        this.permissionLoaded = true; // 标记权限已加载

        if (accessedRoutes.length > 0) {
          if (check === "go") {
            const { path, children }: any = accessedRoutes[0];
            if (path && children.length > 0) {
              // 未访问过系统页面，跳转到权限list首项
              router.replace(path + "/" + children[0].path);
            } else {
              // 未访问过系统页面，无配置权限，跳转个人中心
              router.replace({
                path: "/home/person-center",
              });
            }
            console.log("首次访问");
          } else if (check === "back") {
            // 访问过系统页面
            const redirectUrl: any = sessionStorage.getItem("redirectUrl");
            const success = checkPermissionInRoutes(
              redirectUrl,
              accessedRoutes,
              permissionList
            );
            console.log("访问过系统页面", success, redirectUrl);
            router.replace(success ? redirectUrl : "/");
            // replace逻辑
            // list=>add=>login；
            // login登录成功后，回到add页面，add页面新增后（back）到之前的页面。
          }
        } else {
          console.log("无任何任何权限");
          // 要求，无任何权限的可登录账号可进入个人中心页。
          router.replace({
            path: "/home/person-center",
          });
        }
      }
    },
    async getPersonInfo() {
      const res: any = await axios.get("/facade/sale/agency/user/queryUser");
      if (res.code === 0) {
        const {
          agencyCode, // 单位代码
          agencyId, // 单位ID
          agencyName, // 单位名称
          agencyNameHK,
        } = res.data;
        localStorage.setItem(
          "basic_user_info",
          JSON.stringify({
            agencyCode, // 单位代码
            agencyId, // 单位ID
            agencyName, // 单位名称
            agencyNameHK,
          })
        );
        if (res.data.defaultSetting) {
          if (!res.data.defaultSetting.carType) {
            res.data.defaultSetting.carType = 1;
          }
          if (res.data.defaultSetting.number === null) {
            res.data.defaultSetting.number = 1;
          }
        }
        this.updatePersonInfo(res.data);
      }
    },
    // 请求销售单位对应的列表
    async queryAgentUnitList() {
      const res: any = await axios.get(
        `/facade/sale/agency/getAgenciesIncludeSub`
      );
      if (res.code === 0) {
        const newArray = [] as any;
        res.data.forEach((item: any) => {
          const obj = {
            id: item.agencyId,
            nameCn: item.agencyName,
          };
          newArray.push(obj);
        });
        localStorage.setItem("agency_unit", JSON.stringify(newArray));
      }
    },
    // 获取用户资产信息
    async fetchUserAsset() {
      const res = await Http_getUserAsset();
      if (res.code === 0) {
        const {
          id,
          type,
          currencyType,
          accountBalance,
          openTicketInventory,
          creditLimit,
        } = res.data;
        this.userAsset = {
          id,
          type,
          currencyType,
          accountBalance,
          openTicketInventory: openTicketInventory || 0,
          creditLimit,
        };
      } else {
        this.userAsset = {
          id: 0,
          type: 0,
          currencyType: 0,
          accountBalance: 0,
          openTicketInventory: 0,
          creditLimit: 0,
        };
      }
      return this.userAsset;
    },
  },
});

const hasPermission = (roles: any, permission: string[]) => {
  if (Array.isArray(roles)) {
    return permission.some((role) => roles.includes(role));
  } else {
    return false;
  }
};

function filterRoutesByPermission(
  routes: RouteRecordRaw[],
  permission: string[]
) {
  const filteredRoutes = routes
    .map((route) => {
      if (hasPermission(route.meta?.roles, permission)) {
        // 深拷贝路由对象，避免修改原始路由
        const routeCopy: RouteRecordRaw = {
          ...route,
          // 深拷贝 meta 对象
          meta: route.meta ? { ...route.meta } : undefined,
        };

        if (route.children && route.children.length > 0) {
          // 递归过滤子路由，返回新数组
          routeCopy.children = filterRoutesByPermission(
            route.children,
            permission
          );
        }
        return routeCopy;
      } else {
        return null;
      }
    })
    .filter((route): route is RouteRecordRaw => route !== null);

  return filteredRoutes;
}

/**
 * 检查给定路径是否有权限访问对应的路由
 *
 * 该函数用于检查给定的路径是否有权限访问对应的路由。
 * 它会逐级检查路径中的每个部分，确保每个部分对应的路由都具有所需的权限。
 *
 * @param `path` 要检查权限的路径
 * @param `routes` 路由配置数组
 * @param `permission` 需要的权限数组
 *
 * @returns 返回一个布尔值，表示是否拥有权限访问该路径
 */
function checkPermissionInRoutes(
  path: string,
  routes: RouteRecordRaw[],
  permission: string[]
) {
  if (!path) {
    return false;
  }
  let allPermission = false;
  const parts = path.split("/");
  parts.shift();
  // 判断每级的路由url是否都有权限，
  // 比如url = '/aa/bb/cc?t=123',解析出的 aa, bb, cc对应的路由都要有权限
  for (let i = 0; i < routes.length; i++) {
    const v = routes[i];
    const url = v.path.replace("/", "");
    // 判断第1段路由
    if (parts[0].includes(url) && hasPermission(v.meta?.roles, permission)) {
      if (Array.isArray(v.children)) {
        for (let ii = 0; ii < v.children.length; ii++) {
          const vv = v.children[ii];
          // 判断第2段路由
          if (
            parts[1].includes(vv.path) &&
            hasPermission(vv.meta?.roles, permission)
          ) {
            if (Array.isArray(vv.children)) {
              for (let iii = 0; iii < vv.children.length; iii++) {
                const vvv = vv.children[iii];
                // 判断第3段路由
                if (
                  parts[2].includes(vvv.path) &&
                  hasPermission(vvv.meta?.roles, permission)
                ) {
                  allPermission = true;
                  break;
                }
              }
            } else {
              allPermission = true;
              break;
            }
          }
        }
      } else {
        allPermission = true;
        break;
      }
      break;
    }
  }
  return allPermission;
}
